// 騎士・境界の薙ぎ (lanceSweep) の「重さ」候補の描画側 (2026-09-27 夜・S-1「重さはもっと欲しい」)。
//   候補の切替と絵の側 (タイムライン) = src/boss_pose_timeline.ts KNIGHT_SWEEP_WEIGHT_TIMELINES / DEV `?sweepWeight=a|b|c`。
//   ここは描画だけ = 戦闘の状態 (時間・判定・hitStop・state.shake) には一切触れない。
import type { Boss, GameState } from './types'
import { getKnightSweepVariant, getKnightSweepWeight } from './boss_pose_timeline'

function sweepActiveMs(boss: Boss): number | null {
  if (boss.id !== 'vigil-knight' || boss.hp <= 0 || boss.currentAttack?.id !== 'lanceSweep') return null
  if (getKnightSweepVariant() !== 'keypose') return null
  if (boss.state === 'active') return boss.stateElapsed * 1000
  if (boss.state === 'beatGap' || boss.state === 'recovery') return boss.currentAttack.activeMs + boss.stateElapsed * 1000
  return null
}

// 候補 a: 当たった瞬間の縦の揺れ (世界の層だけ・背景は揺らさない = 既存の画面揺れと同じ扱い)。
//   振幅 3px → 110ms で 0。乱数を使わない (同じ瞬間は同じ揺れ = 撮影で再現できる)。
export const KNIGHT_SWEEP_IMPACT = { amplitudePx: 3, durationMs: 110, hz: 26 } as const
export function knightSweepImpactShake(state: GameState): { x: number; y: number } {
  if (state.phase !== 'playing' || getKnightSweepWeight() !== 'a') return { x: 0, y: 0 }
  const ms = sweepActiveMs(state.boss)
  if (ms === null || ms >= KNIGHT_SWEEP_IMPACT.durationMs) return { x: 0, y: 0 }
  const k = 1 - ms / KNIGHT_SWEEP_IMPACT.durationMs
  const amp = KNIGHT_SWEEP_IMPACT.amplitudePx * k * k
  const w = Math.cos((ms / 1000) * KNIGHT_SWEEP_IMPACT.hz * Math.PI * 2)
  return { x: amp * 0.3 * w, y: amp * w }
}

// 候補 c (と bc): 踏み込んだ前足 (振り終わりの絵の前足 = ボス中心から前 48px・床) から、床沿いに前後へ低く広がる砂ぼこり。
//   前足の位置 = lanceSweep_contact.png の靴底 (不透明 bbox の横 0.27) を実描画の穂先 (前 94) から換算した値 (2026-09-27)。
//   寿命 380ms。色 = 床の石の灰に鎧の影の藤鼠を少し。粒は決め打ちの 9 個 (乱数なし)。
export const KNIGHT_SWEEP_DUST = { footFwdPx: 48, lifeMs: 380 } as const
const DUST_PUFFS: ReadonlyArray<{ dir: number; speed: number; r: number; delay: number }> = [
  { dir: 1, speed: 0.11, r: 7, delay: 0 }, { dir: 1, speed: 0.07, r: 9, delay: 10 }, { dir: 1, speed: 0.04, r: 6, delay: 30 },
  { dir: -1, speed: 0.09, r: 8, delay: 0 }, { dir: -1, speed: 0.05, r: 10, delay: 15 }, { dir: -1, speed: 0.025, r: 6, delay: 40 },
  { dir: 0.4, speed: 0.06, r: 11, delay: 5 }, { dir: -0.4, speed: 0.05, r: 9, delay: 20 }, { dir: 0, speed: 0, r: 12, delay: 0 },
]
export function drawKnightSweepDust(ctx: CanvasRenderingContext2D, state: GameState): void {
  const w = getKnightSweepWeight()
  if (state.phase !== 'playing' || (w !== 'c' && w !== 'bc')) return
  const b = state.boss
  const ms = sweepActiveMs(b)
  if (ms === null || ms >= KNIGHT_SWEEP_DUST.lifeMs) return
  const face = b.facing === 'right' ? 1 : -1
  const footX = b.x + KNIGHT_SWEEP_DUST.footFwdPx * face
  const floorY = b.y
  ctx.save()
  for (const p of DUST_PUFFS) {
    const t = ms - p.delay
    if (t < 0) continue
    const q = Math.min(1, t / (KNIGHT_SWEEP_DUST.lifeMs - p.delay))
    const ease = 1 - (1 - q) * (1 - q)
    const x = footX + p.dir * face * p.speed * (KNIGHT_SWEEP_DUST.lifeMs * ease)
    const rise = 3 + 7 * ease * (1 - Math.abs(p.dir) * 0.5)
    const r = p.r * 1.3 * (0.6 + 0.9 * ease)
    const a = 0.55 * (1 - q) * (1 - q)
    if (a <= 0.005) continue
    const g = ctx.createRadialGradient(x, floorY - rise, 0, x, floorY - rise, r)
    g.addColorStop(0, `rgba(178,170,190,${a})`)
    g.addColorStop(1, 'rgba(120,112,140,0)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.ellipse(x, floorY - rise, r * 1.4, r * 0.75, 0, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}
